import React, { useState } from 'react';
import { downloadEditorialStockPdf } from '../utils/stockReportEditorialPdf';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Check } from 'lucide-react';
import { getPreparedFor } from '../utils/businessIdentity';

/**
 * StockExportReportModal
 * ──────────────────────
 * Lets the user pick individual stock-report sections and choose PDF or Excel format.
 * Sections: Item-Wise Sales, Raw Material Stock, Estimated vs Real-Time Usage
 */
const StockExportReportModal = ({
    isOpen,
    onClose,
    itemWiseSalesData = [],
    rawMaterialStockData = [],
    estimatedRealtimeData = [],
    periodLabel = '',
}) => {
    const SECTIONS = [
        { id: 'item-wise',   label: 'Item-Wise Sales',                group: 'table' },
        { id: 'raw-material', label: 'Raw Material Stock',            group: 'table' },
        { id: 'estimated',    label: 'Estimated vs Real-Time Usage',  group: 'table' },
    ];

    const [selectedSections, setSelectedSections] = useState(SECTIONS.map(s => s.id));
    const [exportFormat, setExportFormat] = useState('pdf');
    const [isExporting, setIsExporting] = useState(false);
    const [exportDone, setExportDone] = useState(false);

    React.useEffect(() => {
        if (isOpen) {
            setSelectedSections(SECTIONS.map(s => s.id));
            setExportDone(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    if (!isOpen) return null;

    const toggleSection = (id) => {
        setSelectedSections(prev =>
            prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
        );
    };

    const toggleAll = () => {
        setSelectedSections(prev =>
            prev.length === SECTIONS.length ? [] : SECTIONS.map(s => s.id)
        );
    };

    const fileTimestamp = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

    // ─── C10. The estimated-cost footnote, on THIS export too ───────────────────────
    // The Sales export has carried this note for a while; the Stock export publishes the
    // same gross-profit figures from the same engine and carried nothing. A forwarded PDF
    // whose margins are partly priced from year-old invoices, with no note saying so, is a
    // trust incident — and the person it misleads is an accountant who never saw the
    // setting that produced it.
    //
    // Sized from the rows, not from a summary the modal is not given: each row carries its
    // own estimatedCogsAmount, computed by the same arithmetic as the summary total, so
    // the two agree.
    const estimatedRows = (itemWiseSalesData || []).filter((it) => it && it.cogsEstimated && !it.cogsUnavailable);
    const estimatedTotal = estimatedRows.reduce((sum, it) => sum + (Number(it.estimatedCogsAmount) || 0), 0);
    const estimatedIngredientNames = [...new Set(
        estimatedRows.flatMap((it) => (it.estimatedIngredients || []).map((e) => e && e.name).filter(Boolean))
    )];
    const estimatedFootnote = estimatedRows.length > 0
        ? `~ Estimated. ${estimatedRows.length} item(s) include ingredients priced at their last known `
          + `purchase price because stock was unavailable and no confirmed closing-stock price exists. `
          + `Estimated cost included: ₹${estimatedTotal.toFixed(2)}.`
          + (estimatedIngredientNames.length
              ? ` Ingredients: ${estimatedIngredientNames.slice(0, 8).join(', ')}`
                + `${estimatedIngredientNames.length > 8 ? `, +${estimatedIngredientNames.length - 8} more` : ''}.`
              : '')
        : null;

    // ═══════════════════════════════════════════════════════
    // ─── PDF Export ───────────────────────────────────────
    // ═══════════════════════════════════════════════════════
    const exportPDF = async () => {
        setIsExporting(true);
        try {
            const preparedFor = await getPreparedFor();
            const generated = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });

            const toNum = (v) => {
                if (typeof v === 'number') return v;
                const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.-]/g, ''));
                return Number.isFinite(n) ? n : 0;
            };

            // Level 1 — item-wise sales. Numeric values live in marginBreakdown; some
            // top-level fields are ₹-strings (and item.grossProfit is actually revenue).
            const itemWiseSales = (itemWiseSalesData || []).map((it) => {
                const mb = it.marginBreakdown || {};
                const approximate = !!it.cogsUnavailable;
                return {
                    name: it.name,
                    qtySold: toNum(it.sold),
                    sellingPrice: mb.sellingPrice != null ? Number(mb.sellingPrice) : toNum(it.price),
                    totalSales: mb.totalRevenue != null ? Number(mb.totalRevenue) : toNum(it.grossProfit),
                    grossProfit: approximate ? 0 : (mb.netGrossProfit != null ? Number(mb.netGrossProfit) : 0),
                    margin: approximate ? null : (it.margin != null ? Number(it.margin) : (mb.netMargin != null ? Number(mb.netMargin) : null)),
                    approximate,
                    // Distinct from `approximate`, which means the cost is UNKNOWN. This row's
                    // figures are real and printed — part of the cost behind them is an
                    // estimate, and the "~" is what says so on the page.
                    estimated: !approximate && !!it.cogsEstimated,
                };
            });

            // Level 2 — raw material deducted, valued from raw base-unit qty × unit cost.
            const rawMaterialDeducted = (rawMaterialStockData || []).map((r) => ({
                name: r.name,
                unit: r.baseUnit || r.beforeUnit || '',
                unitCost: Number(r.unitCost || 0),
                before: Number(r.rawBefore || 0),
                deducted: Number(r.rawDeducted || 0),
                wasted: Number(r.rawWasted || 0),
                after: Number(r.rawAfter || 0),
            }));

            // Level 3 — estimated vs actual plates.
            const estimatedVsActual = (estimatedRealtimeData || []).map((r) => ({
                menuItem: r.menu_item || r.menuItem || r.name || '',
                estimated: Number(r.estimated || 0),
                actual: Number(r.actual_plates != null ? r.actual_plates : (r.actual || 0)),
            }));

            const data = {
                meta: {
                    brand: 'INVEXIS', title: 'Stock Report',
                    period: periodLabel, periodShort: periodLabel,
                    generated, preparedFor, generatedBy: 'Generated by Invexis',
                    kicker: 'Inventory Movement & Usage', heroLine1: 'Stock', heroLine2: 'Report',
                    subtitle: 'Item-wise sales, the value of raw material deducted from stock, and estimated-versus-actual plate usage for the period.',
                },
                itemWiseSales,
                rawMaterialDeducted,
                estimatedVsActual,
                // Travels with the document. Null when nothing was estimated.
                estimatedCogsNote: estimatedFootnote,
            };

            await downloadEditorialStockPdf(data, selectedSections, `Stock_Report_${fileTimestamp}.pdf`);
            setExportDone(true);
        } catch (err) {
            console.error('Stock PDF export failed:', err);
            alert('PDF export failed. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    // ═══════════════════════════════════════════════════════
    // ─── Excel Export ─────────────────────────────────────
    // ═══════════════════════════════════════════════════════
    const exportExcel = () => {
        setIsExporting(true);
        try {
            const wb = XLSX.utils.book_new();
            const safe = (v) => (v != null && v !== '') ? String(v) : '-';

            // Build all sheet data upfront
            const sheets = [];

            // 1. Item-Wise Sales
            if (selectedSections.includes('item-wise')) {
                const data = Array.isArray(itemWiseSalesData) ? itemWiseSalesData : [];
                if (data.length > 0) {
                    // `item.grossProfit` carries REVENUE, so it belongs under Total Amount;
                    // profit gets its own column from grossProfitTotal. The margin column
                    // previously read `item.change`, which items do not carry — it exported
                    // blank for every row.
                    const rows = [['Item', 'Price', 'Sold', 'Total Amount', 'Gross Profit', 'Margin %', 'Cost Basis']];
                    for (let i = 0; i < data.length; i++) {
                        const item = data[i] || {};
                        rows.push([
                            safe(item.name),
                            safe(item.price),
                            safe(item.sold),
                            safe(item.grossProfit),
                            item.grossProfitTotal == null ? '-' : Number(item.grossProfitTotal),
                            item.margin == null ? '-' : Number(Number(item.margin).toFixed(1)),
                            (item.cogsEstimated && !item.cogsUnavailable) ? '~ estimated' : ''
                        ]);
                    }
                    // A spreadsheet gets filtered, sorted and pasted elsewhere, so the mark
                    // has to be a CELL on the row rather than only a note at the bottom —
                    // the note is the first thing lost when someone copies the range.
                    if (estimatedFootnote) rows.push([], [estimatedFootnote]);
                    sheets.push({
                        name: 'Item-Wise Sales',
                        rows: rows,
                        cols: [{ wch: 30 }, { wch: 20 }, { wch: 18 }, { wch: 18 }]
                    });
                }
            }

            // 2. Raw Material Stock
            if (selectedSections.includes('raw-material')) {
                const data = Array.isArray(rawMaterialStockData) ? rawMaterialStockData : [];
                if (data.length > 0) {
                    const rows = [['Material', 'Before', 'Deducted', 'Wasted', 'After', 'Status']];
                    for (let i = 0; i < data.length; i++) {
                        const item = data[i] || {};
                        const fmtVal = (val, unit) => {
                            const v = (val != null && val !== '') ? String(val) : '-';
                            const u = unit ? String(unit) : '';
                            return u ? (v + ' ' + u) : v;
                        };
                        rows.push([
                            safe(item.name),
                            fmtVal(item.before, item.beforeUnit),
                            fmtVal(item.deducted, item.deductedUnit),
                            fmtVal(item.wasted, item.wastedUnit),
                            fmtVal(item.after, item.afterUnit),
                            safe(item.status)
                        ]);
                    }
                    sheets.push({
                        name: 'Raw Material Stock',
                        rows: rows,
                        cols: [{ wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }]
                    });
                }
            }

            // 3. Estimated vs Real-Time Usage
            if (selectedSections.includes('estimated')) {
                const data = Array.isArray(estimatedRealtimeData) ? estimatedRealtimeData : [];
                if (data.length > 0) {
                    const rows = [['Menu Item', 'Estimated (plates)', 'Actual (plates)', 'Variance']];
                    for (let i = 0; i < data.length; i++) {
                        const item = data[i] || {};
                        const est = Number(item.estimated || 0);
                        const act = Number(item.actual_plates || 0);
                        const variance = act - est;
                        rows.push([
                            safe(item.menu_item),
                            est,
                            act,
                            variance
                        ]);
                    }
                    sheets.push({
                        name: 'Estimated vs Actual',
                        rows: rows,
                        cols: [{ wch: 30 }, { wch: 18 }, { wch: 18 }, { wch: 15 }]
                    });
                }
            }

            // Add all sheets to workbook
            if (sheets.length === 0) {
                const ws = XLSX.utils.aoa_to_sheet([['No data available for selected sections']]);
                XLSX.utils.book_append_sheet(wb, ws, 'Info');
            } else {
                for (let s = 0; s < sheets.length; s++) {
                    try {
                        const ws = XLSX.utils.aoa_to_sheet(sheets[s].rows);
                        ws['!cols'] = sheets[s].cols;
                        XLSX.utils.book_append_sheet(wb, ws, sheets[s].name);
                    } catch (sheetErr) {
                        console.error('Failed to add sheet: ' + sheets[s].name, sheetErr);
                    }
                }
            }

            const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `Stock_Report_${fileTimestamp}.xlsx`);
            setExportDone(true);
        } catch (err) {
            console.error('Excel export failed:', err);
            alert('Excel export failed. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const handleExport = () => {
        if (exportFormat === 'pdf') exportPDF();
        else exportExcel();
    };

    // ─── Done Screen ──────────────────────────────────────
    if (exportDone) {
        return (
            <div style={overlayStyle} onClick={() => { setExportDone(false); onClose(); }}>
                <div style={modalStyle} onClick={e => e.stopPropagation()}>
                    <div style={{ textAlign: 'center', padding: '24px 0' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                            <div style={{
                                width: 60, height: 60, borderRadius: '50%',
                                border: '3px solid #22c55e', backgroundColor: 'white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <Check size={36} color="#22c55e" strokeWidth={3} />
                            </div>
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
                            Report Downloaded!
                        </div>
                        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
                            {exportFormat === 'pdf' ? 'PDF' : 'Excel (.xlsx)'} file saved to your Downloads folder.
                        </div>
                        <button onClick={() => { setExportDone(false); onClose(); }} style={primaryBtnStyle}>OK</button>
                    </div>
                </div>
            </div>
        );
    }

    // ─── Main Modal ──────────────────────────────────────
    return (
        <div style={overlayStyle} onClick={onClose}>
            <div style={modalStyle} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#1f2937' }}>Export Stock Report</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>&times;</button>
                </div>

                {/* Format Toggle */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                    {[
                        { key: 'pdf', label: 'PDF', desc: 'Tables + Report' },
                        { key: 'excel', label: 'Excel', desc: 'Data Sheets (.xlsx)' },
                    ].map(f => (
                        <button
                            key={f.key}
                            onClick={() => setExportFormat(f.key)}
                            style={{
                                flex: 1, padding: '12px 8px', borderRadius: 10,
                                border: `2px solid ${exportFormat === f.key ? '#000' : '#e5e7eb'}`,
                                background: exportFormat === f.key ? '#f3f4f6' : '#fff',
                                cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center',
                            }}
                        >
                            <div style={{ fontSize: 15, fontWeight: 700, color: exportFormat === f.key ? '#000' : '#374151' }}>{f.label}</div>
                            <div style={{ fontSize: 11, color: exportFormat === f.key ? '#374151' : '#9ca3af', marginTop: 2 }}>{f.desc}</div>
                        </button>
                    ))}
                </div>

                {/* Section Selection */}
                <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Select Sections to Export</span>
                        <button onClick={toggleAll} style={{ background: 'none', border: 'none', color: '#000', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            {selectedSections.length === SECTIONS.length ? 'Deselect All' : 'Select All'}
                        </button>
                    </div>

                    {SECTIONS.map(section => {
                        const dataCount =
                            section.id === 'item-wise' ? (itemWiseSalesData?.length || 0) :
                            section.id === 'raw-material' ? (rawMaterialStockData?.length || 0) :
                            section.id === 'estimated' ? (Array.isArray(estimatedRealtimeData) ? estimatedRealtimeData.length : 0) : 0;

                        return (
                            <label key={section.id} style={checkboxRowStyle(selectedSections.includes(section.id))}>
                                <input type="checkbox" checked={selectedSections.includes(section.id)} onChange={() => toggleSection(section.id)} style={checkboxStyle} />
                                <span style={{ fontSize: 13.5, fontWeight: 500, color: '#374151', flex: 1 }}>{section.label}</span>
                                <span style={badgeStyle}>
                                    {dataCount > 0 ? `${dataCount} items` : 'No data'}
                                </span>
                            </label>
                        );
                    })}
                </div>

                {/* Period Info */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 12px', marginBottom: 16,
                    background: '#f5f5f5', borderRadius: 8,
                    fontSize: 12, color: '#6b7280',
                }}>
                    <span>Period: <strong style={{ color: '#374151' }}>{periodLabel}</strong></span>
                </div>

                {/* Export Button */}
                <button
                    onClick={handleExport}
                    disabled={isExporting || selectedSections.length === 0}
                    style={{
                        ...primaryBtnStyle,
                        width: '100%', padding: '14px 24px', fontSize: 15,
                        opacity: (isExporting || selectedSections.length === 0) ? 0.6 : 1,
                        cursor: (isExporting || selectedSections.length === 0) ? 'not-allowed' : 'pointer',
                    }}
                >
                    {isExporting
                        ? (exportFormat === 'pdf' ? 'Generating PDF...' : 'Generating Excel...')
                        : `Export as ${exportFormat === 'pdf' ? 'PDF' : 'Excel'} (${selectedSections.length} section${selectedSections.length !== 1 ? 's' : ''})`
                    }
                </button>
            </div>
        </div>
    );
};

// ─── Styles ─────────────────────────────────────────────
const overlayStyle = {
    position: 'fixed', inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 99999, padding: 16,
    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
};

const modalStyle = {
    width: 'min(440px, 94vw)',
    background: '#fff', borderRadius: '0.5rem',
    boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
    padding: '24px 20px',
    maxHeight: '88vh', overflowY: 'auto',
};

const primaryBtnStyle = {
    padding: '12px 24px',
    backgroundColor: '#000000', color: '#fff',
    border: 'none', borderRadius: 10,
    fontSize: 14, fontWeight: 700, cursor: 'pointer',
};

const checkboxRowStyle = (isChecked) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '11px 12px', marginBottom: 5, borderRadius: 10,
    background: isChecked ? '#f3f4f6' : '#f9fafb',
    border: `1.5px solid ${isChecked ? '#9ca3af' : '#e5e7eb'}`,
    cursor: 'pointer', transition: 'all 0.15s',
});

const checkboxStyle = { width: 17, height: 17, accentColor: '#000', cursor: 'pointer' };

const badgeStyle = { fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap' };

export default StockExportReportModal;
